import { useEffect, useState } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store } from './store/store';
import { fetchClasses, createClass } from './store/slices/classesSlice';
import { fetchInstructors } from './store/slices/instructorsSlice';
import { fetchLocations, createLocation } from './store/slices/locationsSlice';
import { restoreAuth, logout } from './store/slices/authSlice';
import { LOCATIONS } from './utils/constants';
import CalendarGrid from './components/calendar/CalendarGrid';
import PublicCalendar from './components/calendar/PublicCalendar';
import ClassDetailsPanel from './components/class-panel/ClassDetailsPanel';
import Login from './components/auth/Login';
import Loader from './components/loader/Loader';
import AdminUsers from './components/admin/AdminUsers';
import InstagramPosts from './components/instagram/InstagramPosts';
import Attendance from './components/attendance/Attendance';
import StudentsScreen from './components/students/StudentsScreen';
import InstructorPortal from './components/instructor/InstructorPortal';
import SignInLink from './components/parent/SignInLink';
import Ceremonies from './components/ceremony/Ceremonies';
import Competition from './components/competition/Competition';
import GlobalSearch from './components/search/GlobalSearch';
import './App.css';

const AppContent = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { classes, loading } = useSelector((state) => state.classes);
  const [showAdminUsers, setShowAdminUsers] = useState(false);
  const [showInstagram, setShowInstagram] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [showStudents, setShowStudents] = useState(false);
  const [showCeremonies, setShowCeremonies] = useState(false);
  const [showCompetition, setShowCompetition] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    // Restore authentication from localStorage on app load
    dispatch(restoreAuth());
    
    // Listen for pathname changes (for browser navigation)
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };
    
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, [dispatch]);

  useEffect(() => {
    // Only fetch data if authenticated
    if (!isAuthenticated) {
      return;
    }
    // Fetch instructors from backend on app load
    dispatch(fetchInstructors());
    
    // Fetch locations from backend on app load
    dispatch(fetchLocations()).then((result) => {
      // Check if default locations exist, and seed missing ones
      if (result.type === 'locations/fetchLocations/fulfilled' && result.payload) {
        const existingLocations = result.payload.map(loc => loc.name);
        const missingLocations = LOCATIONS.filter(locName => !existingLocations.includes(locName));
        
        // Seed any missing default locations
        if (missingLocations.length > 0) {
          missingLocations.forEach((locationName) => {
            dispatch(createLocation({
              name: locationName,
              isActive: true,
            }));
          });
          // Refresh locations after seeding
          setTimeout(() => {
            dispatch(fetchLocations());
          }, 500);
        }
      }
    });
    
    // Fetch classes from backend on app load
    dispatch(fetchClasses()).then((result) => {
      // If API succeeds but returns empty, create sample data in backend
      if (result.type === 'classes/fetchClasses/fulfilled' && (!result.payload || result.payload.length === 0)) {
        // Create sample classes in backend
        const sampleClasses = [
          {
            type: 'Bhangra',
            ageGroup: 'Teens',
            instructor: 'Pratap',
            day: 'Monday',
            startTime: '17:00',
            duration: 60,
            location: 'Studio A',
            notes: 'Focus on footwork this week',
            rumaalLevel: 'Green',
            name: 'Advanced Teens',
            students: [
              {
                name: 'Raj Patel',
                rumaalRank: 'Green',
                status: 'Active',
              },
              {
                name: 'Priya Singh',
                rumaalRank: 'Blue',
                status: 'Active',
              },
            ],
          },
          {
            type: 'Bollywood',
            ageGroup: 'Adults',
            instructor: 'Gugu',
            day: 'Wednesday',
            startTime: '19:00',
            duration: 90,
            location: 'Studio B',
            notes: '',
            rumaalLevel: 'Yellow',
            name: 'Bollywood Adults',
            students: [
              {
                name: 'Amit Kumar',
                rumaalRank: 'Yellow',
                status: 'Trial',
              },
            ],
          },
        ];
        
        // Save each sample class to backend
        sampleClasses.forEach((classData) => {
          dispatch(createClass(classData));
        });
      }
    });
  }, [dispatch, isAuthenticated]);

  // Show public calendar if on /calendar route (no authentication required)
  if (currentPath === '/calendar') {
    return <PublicCalendar />;
  }

  // The wall-mounted instructor iPad. Deliberately ahead of the auth check —
  // it authenticates with a registered device token, and an instructor must
  // never be shown a login screen mid-class.
  if (currentPath === '/instructor') {
    return <InstructorPortal />;
  }

  // Sign-in and invitation links. Ahead of the auth check for the same reason
  // the iPad is: the person following one is a parent, and the admin login is
  // the last thing they should be looking at. On a phone with the app this page
  // never renders — iOS opens The Stable straight from the link.
  if (currentPath === '/parent/auth') {
    return <SignInLink />;
  }

  // Show login page if not authenticated (for admin routes)
  if (!isAuthenticated) {
    return <Login />;
  }

  // Show loader while data is being fetched on initial render
  if (loading && classes.length === 0 && !showAdminUsers && !showInstagram && !showAttendance && !showStudents && !showCeremonies && !showCompetition) {
    return <Loader />;
  }

  // Show Ceremonies view
  if (showCeremonies) {
    return (
      <div className="App flex flex-col">
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">Rumaal Rank</h1>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">Logged in as: {user?.username}</span>
              <button
                onClick={() => setShowCeremonies(false)}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Back to Calendar
              </button>
              <button
                onClick={() => {
                  dispatch(logout());
                  setShowCeremonies(false);
                }}
                className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <Ceremonies />
        </div>
      </div>
    );
  }

  // Show Competition view
  if (showCompetition) {
    return (
      <div className="App flex flex-col">
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">Competition</h1>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">Logged in as: {user?.username}</span>
              <button
                onClick={() => setShowCompetition(false)}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Back to Calendar
              </button>
              <button
                onClick={() => {
                  dispatch(logout());
                  setShowCompetition(false);
                }}
                className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <Competition />
        </div>
      </div>
    );
  }

  // Show Students view
  if (showStudents) {
    return (
      <div className="App flex flex-col">
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">Students</h1>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">Logged in as: {user?.username}</span>
              <button
                onClick={() => setShowStudents(false)}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Back to Calendar
              </button>
              <button
                onClick={() => {
                  dispatch(logout());
                  setShowStudents(false);
                }}
                className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <StudentsScreen />
        </div>
      </div>
    );
  }

  // Show Admin Users view
  if (showAdminUsers) {
    return (
      <div className="App flex flex-col">
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">Admin Settings</h1>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">Logged in as: {user?.username}</span>
              <button
                onClick={() => setShowAdminUsers(false)}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Back to Calendar
              </button>
              <button
                onClick={() => {
                  dispatch(logout());
                  setShowAdminUsers(false);
                }}
                className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <AdminUsers />
        </div>
      </div>
    );
  }

  // Show Attendance view
  if (showAttendance) {
    return (
      <div className="App flex flex-col">
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">Attendance</h1>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">Logged in as: {user?.username}</span>
              <button
                onClick={() => setShowAttendance(false)}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Back to Calendar
              </button>
              <button
                onClick={() => {
                  dispatch(logout());
                  setShowAttendance(false);
                }}
                className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <Attendance />
        </div>
      </div>
    );
  }

  // Show Instagram posts view
  if (showInstagram) {
    return (
      <div className="App flex flex-col">
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">Instagram</h1>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">Logged in as: {user?.username}</span>
              <button
                onClick={() => setShowInstagram(false)}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Back to Calendar
              </button>
              <button
                onClick={() => {
                  dispatch(logout());
                  setShowInstagram(false);
                }}
                className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <InstagramPosts />
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {/* Header with Settings button */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 flex items-center justify-between gap-2 sm:gap-3">
          <GlobalSearch />
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <span className="hidden md:inline text-sm text-gray-600">Logged in as: {user?.username}</span>
            <button
              onClick={() => setShowCeremonies(true)}
              className="px-2.5 sm:px-4 py-2 text-sm bg-amber-100 text-amber-700 rounded hover:bg-amber-200 transition-colors flex items-center gap-2"
              title="Rumaal Rank"
              aria-label="Rumaal Rank"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              <span className="hidden sm:inline">Rumaal Rank</span>
            </button>
            <button
              onClick={() => setShowCompetition(true)}
              className="px-2.5 sm:px-4 py-2 text-sm bg-rose-100 text-rose-700 rounded hover:bg-rose-200 transition-colors flex items-center gap-2"
              title="Competition"
              aria-label="Competition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              <span className="hidden sm:inline">Competition</span>
            </button>
            <button
              onClick={() => setShowStudents(true)}
              className="px-2.5 sm:px-4 py-2 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors flex items-center gap-2"
              title="Students"
              aria-label="Students"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <span className="hidden sm:inline">Students</span>
            </button>
            <button
              onClick={() => setShowAttendance(true)}
              className="px-2.5 sm:px-4 py-2 text-sm bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 transition-colors flex items-center gap-2"
              title="Attendance"
              aria-label="Attendance"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 5a2 2 0 002 2h2a2 2 0 002-2m-6 8l2 2 4-4" />
              </svg>
              <span className="hidden sm:inline">Attendance</span>
            </button>
            <button
              onClick={() => setShowInstagram(true)}
              className="px-2.5 sm:px-4 py-2 text-sm bg-pink-100 text-pink-700 rounded hover:bg-pink-200 transition-colors flex items-center gap-2"
              title="Instagram Posts"
              aria-label="Instagram Posts"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="3" y="3" width="18" height="18" rx="5" strokeWidth={2} />
                <circle cx="12" cy="12" r="3.5" strokeWidth={2} />
                <path strokeLinecap="round" strokeWidth={2} d="M16.9 7.1h.01" />
              </svg>
              <span className="hidden sm:inline">Instagram</span>
            </button>
            <button
              onClick={() => setShowAdminUsers(true)}
              className="px-2.5 sm:px-4 py-2 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors flex items-center gap-2"
              title="Admin Settings"
              aria-label="Admin Settings"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="hidden sm:inline">Admin Settings</span>
            </button>
            <button
              onClick={() => dispatch(logout())}
              className="px-2.5 sm:px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
      <CalendarGrid />
      <ClassDetailsPanel />
    </div>
  );
};

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
